import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowsClockwise, Download, FileText, Trash, Lock, LockOpen } from "@phosphor-icons/react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/fmt";

/**
 * Phase 4 + 28.9 — driver personal invoice list with draft / issued
 * lifecycle. Reads `/api/driver/invoices`.
 *
 * Statuses surfaced:
 *   • DRAFT  → editable, no number, can be deleted, "Émettre" button.
 *   • ISSUED → locked, has FAC-YYYY-NNN number, immutable; only PDF
 *              download (no edit, no delete; corrections via avoir).
 *
 * Strict isolation: never touched by admin endpoints. PDF for issued
 * invoices is rendered from a frozen issuer snapshot, so it stays byte
 * stable even if the driver later edits their profile.
 */
function fmtEur(n) {
  const v = Number(n || 0);
  return `${v.toFixed(2).replace(/\.00$/, "")} €`;
}

function statusOf(inv) {
  return inv.status || (inv.number ? "issued" : "draft");
}

async function downloadInvoice(inv) {
  try {
    const res = await api.get(`/driver/invoices/${inv.id}/pdf`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${inv.number || "brouillon"}.pdf`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Échec du téléchargement");
  }
}

export default function PersonalInvoices({ refreshKey = 0 }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/driver/invoices");
      setInvoices(data || []);
    } catch {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices, refreshKey]);

  const remove = async (inv) => {
    if (!window.confirm("Supprimer ce brouillon de facture ?")) return;
    try {
      await api.delete(`/driver/invoices/${inv.id}`);
      toast.success("Brouillon supprimé");
      fetchInvoices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la suppression");
    }
  };

  const issue = async (inv) => {
    if (!window.confirm(
      "Cette action verrouille définitivement la facture, lui attribue un numéro FAC-YYYY-NNN et la rend non modifiable. Continuer ?"
    )) return;
    try {
      await api.post(`/driver/invoices/${inv.id}/issue`);
      toast.success("Facture émise et verrouillée");
      fetchInvoices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Émission échouée");
    }
  };

  return (
    <div data-testid="personal-invoices" className="space-y-3 pt-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-amber-400" />
          <p className="text-sm font-medium text-white">Mes factures</p>
          <span className="text-[11px] text-zinc-500">({invoices.length})</span>
        </div>
        <Button data-testid="invoices-refresh" onClick={fetchInvoices} disabled={loading}
          variant="outline" className="h-8 px-2 border-zinc-700 text-zinc-300 hover:bg-zinc-800">
          <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
        </Button>
      </div>

      {invoices.length === 0 ? (
        <p data-testid="invoices-empty" className="text-xs text-zinc-500 italic px-1">
          Aucune facture. Terminez une course personnelle puis cliquez sur « Facturer ».
        </p>
      ) : (
        <div className="space-y-2">
          {invoices.map((inv) => {
            const st = statusOf(inv);
            const isDraft = st === "draft";
            return (
              <div key={inv.id} data-testid={`invoice-row-${inv.id}`}
                className="bg-[#121212] border border-zinc-800 rounded-md p-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {isDraft ? (
                      <span data-testid={`invoice-status-${inv.id}`}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">
                        <LockOpen size={10} /> Brouillon
                      </span>
                    ) : (
                      <span data-testid={`invoice-status-${inv.id}`}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                        <Lock size={10} /> Émise
                      </span>
                    )}
                    {inv.number ? (
                      <span className="text-xs font-mono text-amber-300">{inv.number}</span>
                    ) : (
                      <span className="text-[11px] text-zinc-500 italic">numéro attribué à l'émission</span>
                    )}
                    {!isDraft && (
                      <span className="text-[10px] text-zinc-500">émise le {fmtDate(inv.issued_at)}</span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-300 truncate mt-0.5">
                    {inv.client_name || "(client sans nom)"} · <span className="text-amber-200">{fmtEur(inv.amount)}</span>
                  </p>
                </div>
                <div className="flex gap-1.5 shrink-0 flex-wrap">
                  {isDraft && (
                    <Button data-testid={`invoice-issue-${inv.id}`} onClick={() => issue(inv)}
                      className="h-8 px-2 text-xs bg-emerald-500/15 text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/25">
                      <Lock size={12} className="mr-1" /> Émettre
                    </Button>
                  )}
                  <Button data-testid={`invoice-download-${inv.id}`} onClick={() => downloadInvoice(inv)}
                    variant="outline" className="h-8 px-2 text-xs border-amber-500/30 text-amber-200 hover:bg-amber-500/10">
                    <Download size={12} className="mr-1" /> PDF
                  </Button>
                  {isDraft && (
                    <Button data-testid={`invoice-delete-${inv.id}`} onClick={() => remove(inv)}
                      variant="outline" className="h-8 px-2 text-xs border-red-500/30 text-red-300 hover:bg-red-500/10">
                      <Trash size={12} />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
